/**
 * Scrolls to an element by ID with retry logic for dynamic content loading
 * @param elementId - The ID of the element to scroll to
 * @param options - ScrollIntoView options (defaults to smooth scroll to start)
 * @param retryDelays - Array of retry delays in milliseconds (defaults to [100, 300, 500, 1000, 2000])
 * @returns Promise that resolves when scroll is complete or element is not found
 */
export const scrollToElement = (
    elementId: string,
    options: ScrollIntoViewOptions = { behavior: 'smooth', block: 'start' },
    retryDelays: number[] = [100, 300, 500, 1000, 2000]
): Promise<boolean> => {
    return new Promise(resolve => {
        const scrollToTarget = (): boolean => {
            const target = document.getElementById(elementId);
            if (target) {
                target.scrollIntoView(options);
                return true;
            }
            return false;
        };

        // Try immediately first
        if (scrollToTarget()) {
            resolve(true);
            return;
        }

        // If not found, retry with increasing delays
        let retryCount = 0;

        const retryScroll = () => {
            if (retryCount >= retryDelays.length) {
                resolve(false);
                return;
            }

            setTimeout(() => {
                if (!scrollToTarget()) {
                    retryCount++;
                    retryScroll();
                } else {
                    resolve(true);
                }
            }, retryDelays[retryCount]);
        };

        retryScroll();
    });
};

/**
 * Scrolls to an element based on URL hash with retry logic
 * @param options - ScrollIntoView options (defaults to smooth scroll to start)
 * @param retryDelays - Array of retry delays in milliseconds (defaults to [100, 300, 500, 1000, 2000])
 * @returns Promise that resolves when scroll is complete or no hash is present
 */
export const scrollToElementFromHash = (
    options: ScrollIntoViewOptions = { behavior: 'smooth', block: 'start' },
    retryDelays: number[] = [100, 300, 500, 1000, 2000]
): Promise<boolean> => {
    const hash = window.location.hash?.replace('#', '');
    if (!hash) {
        return Promise.resolve(false);
    }

    return scrollToElement(hash, options, retryDelays);
};
